import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getGuidedQuestions,
  requestGuidedInterpretation,
  requestFreeQuestionInterpretation,
} from '../services/synastryInterpretationApi';
import { localizedServerContent, localizedValue } from '../localization/presentationLanguage';
const synText = (es, en) => localizedValue({ es, en });

function normalizeQuestionId(question) {
  return String(question?.id || '').trim();
}

function buildReadingFingerprint(reading) {
  if (!reading || typeof reading !== 'object') return '';

  const compatibility = reading?.compatibility || {};
  const metadata = reading?.metadata || {};
  const personA = metadata?.personA || {};
  const personB = metadata?.personB || {};

  return JSON.stringify({
    score: Number(compatibility?.masterScore || 0),
    label: String(compatibility?.label || '').trim(),
    interpretation: String(compatibility?.interpretation || '').trim(),
    personA: {
      nickname: String(personA?.nickname || '').trim(),
      birthDate: String(personA?.birthDate || '').trim(),
      sunSign: String(personA?.sunSign || '').trim(),
    },
    personB: {
      nickname: String(personB?.nickname || '').trim(),
      birthDate: String(personB?.birthDate || '').trim(),
      sunSign: String(personB?.sunSign || '').trim(),
    },
    topAspect: Array.isArray(reading?.topAspects) && reading.topAspects[0]
      ? {
          planetA: String(reading.topAspects[0]?.planetA || '').trim(),
          planetB: String(reading.topAspects[0]?.planetB || '').trim(),
          aspectLabel: String(reading.topAspects[0]?.aspectLabel || '').trim(),
          orb: Number(reading.topAspects[0]?.orb || 0),
        }
      : null,
  });
}

function normalizeDailyUsage(value, fallback = { used: 0, limit: 10, remaining: 10, resetAt: null }) {
  const base = fallback && typeof fallback === 'object'
    ? fallback
    : { used: 0, limit: 10, remaining: 10, resetAt: null };

  const limit = Math.max(1, Number(value?.limit ?? base.limit ?? 10) || 10);
  const used = Math.max(0, Number(value?.used ?? base.used ?? 0) || 0);
  const remaining = Math.max(
    0,
    Number(value?.remaining ?? Math.max(0, limit - used)) || 0,
  );

  return {
    used,
    limit,
    remaining,
    resetAt: value?.resetAt ?? base.resetAt ?? null,
  };
}

export default function useSynastryInterpretation() {
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [questionsLoaded, setQuestionsLoaded] = useState(false);
  const [activeQuestionId, setActiveQuestionId] = useState('');
  const [loadingQuestionId, setLoadingQuestionId] = useState('');
  const [interpretationByQuestionId, setInterpretationByQuestionId] = useState({});
  const [askedQuestionIds, setAskedQuestionIds] = useState([]);
  const [error, setError] = useState('');
  const [dailyUsage, setDailyUsage] = useState({ used: 0, limit: 10, remaining: 10, resetAt: null });
  const [freeQuestionInput, setFreeQuestionInput] = useState('');
  const [loadingFreeQuestion, setLoadingFreeQuestion] = useState(false);
  const [freeQuestionHistory, setFreeQuestionHistory] = useState([]);

  const readingRef = useRef(null);
  const readingFingerprintRef = useRef('');
  const inflightQuestionsRef = useRef(null);
  const inflightInterpretationRef = useRef(new Map());
  const inflightFreeQuestionRef = useRef(null);
  const questionsRef = useRef([]);
  const questionsLoadedRef = useRef(false);
  const interpretationByQuestionIdRef = useRef({});
  const freeQuestionHistoryRef = useRef([]);

  useEffect(() => {
    questionsRef.current = questions;
  }, [questions]);

  useEffect(() => {
    questionsLoadedRef.current = questionsLoaded;
  }, [questionsLoaded]);

  useEffect(() => {
    interpretationByQuestionIdRef.current = interpretationByQuestionId;
  }, [interpretationByQuestionId]);

  useEffect(() => {
    freeQuestionHistoryRef.current = freeQuestionHistory;
  }, [freeQuestionHistory]);

  const resetState = useCallback(() => {
    setQuestions([]);
    questionsRef.current = [];
    setLoadingQuestions(false);
    setQuestionsLoaded(false);
    questionsLoadedRef.current = false;
    setActiveQuestionId('');
    setLoadingQuestionId('');
    setInterpretationByQuestionId({});
    interpretationByQuestionIdRef.current = {};
    setAskedQuestionIds([]);
    setError('');
    setDailyUsage({ used: 0, limit: 10, remaining: 10, resetAt: null });
    setFreeQuestionInput('');
    setLoadingFreeQuestion(false);
    setFreeQuestionHistory([]);
    inflightFreeQuestionRef.current = null;
    inflightQuestionsRef.current = null;
    inflightInterpretationRef.current = new Map();
  }, []);

  const setReading = useCallback((reading) => {
    readingRef.current = reading || null;
    readingFingerprintRef.current = buildReadingFingerprint(reading);
  }, []);

  const clearInterpretations = useCallback(() => {
    setActiveQuestionId('');
    setLoadingQuestionId('');
    setInterpretationByQuestionId({});
    setAskedQuestionIds([]);
    setError('');
    setDailyUsage((current) => normalizeDailyUsage(current));
    setFreeQuestionInput('');
    setLoadingFreeQuestion(false);
    setFreeQuestionHistory([]);
    inflightFreeQuestionRef.current = null;
    inflightInterpretationRef.current = new Map();
  }, []);

  const hardReset = useCallback((reading = null) => {
    resetState();
    setReading(reading);
  }, [resetState, setReading]);

  const loadQuestions = useCallback(async (reading, options = {}) => {
    const force = options?.force === true;
    const nextReading = reading || readingRef.current;

    if (!nextReading || typeof nextReading !== 'object') {
      setError('No hay una lectura profunda lista para cargar preguntas guiadas.');
      return { ok: false, questions: [] };
    }

    const nextFingerprint = buildReadingFingerprint(nextReading);
    const readingChanged = nextFingerprint !== readingFingerprintRef.current;

    if (readingChanged) {
      resetState();
      readingRef.current = nextReading;
      readingFingerprintRef.current = nextFingerprint;
    } else if (!readingRef.current) {
      readingRef.current = nextReading;
      readingFingerprintRef.current = nextFingerprint;
    }

    if (!force && questionsLoadedRef.current && questionsRef.current.length > 0 && !readingChanged) {
      return { ok: true, questions: questionsRef.current };
    }

    if (inflightQuestionsRef.current && !force) {
      return inflightQuestionsRef.current;
    }

    setLoadingQuestions(true);
    setError('');

    const promise = getGuidedQuestions(nextReading)
      .then((payload) => {
        const nextQuestions = Array.isArray(payload?.questions) ? payload.questions : [];

        questionsRef.current = nextQuestions;
        questionsLoadedRef.current = true;
        setQuestions(nextQuestions);
        setQuestionsLoaded(true);
        setDailyUsage((current) => normalizeDailyUsage(payload?.dailyUsage, current));

        return { ok: true, questions: nextQuestions, payload };
      })
      .catch((err) => {
        setError(err?.message || 'No se pudieron cargar las preguntas guiadas.');
        return { ok: false, questions: [], error: err };
      })
      .finally(() => {
        setLoadingQuestions(false);
        inflightQuestionsRef.current = null;
      });

    inflightQuestionsRef.current = promise;
    return promise;
  }, [resetState]);

  const askQuestion = useCallback(async (question, options = {}) => {
    const questionId = normalizeQuestionId(question);
    const reading = options?.reading || readingRef.current;

    if (!questionId) {
      setError(synText('La pregunta guiada no es válida.', 'The guided question is invalid.'));
      return { ok: false, error: new Error('Invalid question id') };
    }

    if (!reading || typeof reading !== 'object') {
      setError(synText('No hay una lectura profunda disponible para interpretar.', 'No deep reading is available to interpret.'));
      return { ok: false, error: new Error('Missing reading') };
    }

    if (interpretationByQuestionIdRef.current[questionId] && options?.force !== true) {
      setActiveQuestionId(questionId);
      setDailyUsage((current) => normalizeDailyUsage(current));
      return {
        ok: true,
        cached: true,
        data: interpretationByQuestionIdRef.current[questionId],
      };
    }

    if (inflightInterpretationRef.current.has(questionId) && options?.force !== true) {
      return inflightInterpretationRef.current.get(questionId);
    }

    setError('');
    setActiveQuestionId(questionId);
    setLoadingQuestionId(questionId);

    const promise = requestGuidedInterpretation({
      reading,
      question,
      readingFingerprint: readingFingerprintRef.current || buildReadingFingerprint(reading),
    })
      .then((payload) => {
        const result = payload?.data || null;

        if (!result || typeof result !== 'object') {
          throw new Error(synText('La interpretación guiada llegó vacía.', 'The guided interpretation was empty.'));
        }

        setInterpretationByQuestionId((current) => {
          const nextValue = {
            ...current,
            [questionId]: {
              ...result,
              cached: Boolean(payload?.cached),
              fallback: Boolean(payload?.fallback),
              question,
            },
          };
          interpretationByQuestionIdRef.current = nextValue;
          return nextValue;
        });

        setAskedQuestionIds((current) => (current.includes(questionId) ? current : [...current, questionId]));

        setDailyUsage((current) => normalizeDailyUsage(payload?.dailyUsage, current));

        return { ok: true, data: result, payload };
      })
      .catch((err) => {
        setError(localizedServerContent(err?.message, synText('No se pudo generar la interpretación guiada.', 'The guided interpretation could not be generated.')));
        if (err?.dailyUsage) {
          setDailyUsage((current) => normalizeDailyUsage(err.dailyUsage, current));
        }
        return { ok: false, error: err };
      })
      .finally(() => {
        setLoadingQuestionId((current) => (current === questionId ? '' : current));
        inflightInterpretationRef.current.delete(questionId);
      });

    inflightInterpretationRef.current.set(questionId, promise);
    return promise;
  }, []);

  const askFreeQuestion = useCallback(async (input, options = {}) => {
    const reading = options?.reading || readingRef.current;
    const questionText = String(input || '').trim();

    if (!questionText) {
      setError(synText('La pregunta no es válida.', 'The question is invalid.'));
      return { ok: false, error: new Error('Invalid free question') };
    }

    if (!reading || typeof reading !== 'object') {
      setError('No hay una lectura disponible para esta consulta.');
      return { ok: false, error: new Error('Missing reading') };
    }

    if (inflightFreeQuestionRef.current) {
      return inflightFreeQuestionRef.current;
    }

    setError('');
      setLoadingFreeQuestion(true);
      
          const latestContextEntry = Array.isArray(freeQuestionHistoryRef.current) && freeQuestionHistoryRef.current.length > 0
      ? freeQuestionHistoryRef.current[0]
      : null;

    const previousContext = latestContextEntry
      ? {
          question: String(latestContextEntry?.question || '').trim(),
          answer: String(latestContextEntry?.answer?.answer || '').trim(),
        }
      : null;

    const promise = requestFreeQuestionInterpretation({
      reading,
      freeQuestion: questionText,
        readingFingerprint: readingFingerprintRef.current || buildReadingFingerprint(reading),
            previousContext,
    })
      .then((payload) => {
        const result = payload?.data || null;

        if (!result || typeof result !== 'object') {
          throw new Error(synText('La respuesta de la pregunta libre llegó vacía.', 'The free-form answer was empty.'));
        }

        setFreeQuestionHistory((current) => {
          const nextEntry = {
            question: questionText,
            answer: result,
            cached: Boolean(payload?.cached),
            fallback: Boolean(payload?.fallback),
            timestamp: Date.now(),
          };

          const filtered = current.filter((item) => {
            const sameQuestion = String(item?.question || '').trim().toLowerCase() === questionText.toLowerCase();
            const sameAnswer = String(item?.answer?.answer || '').trim() === String(result?.answer || '').trim();
            return !(sameQuestion && sameAnswer);
          });

          return [nextEntry, ...filtered];
        });

        setDailyUsage((current) => normalizeDailyUsage(payload?.dailyUsage, current));

        return { ok: true, data: result, payload };
      })
      .catch((err) => {
        setError(localizedServerContent(err?.message, synText('No se pudo responder la pregunta.', 'The question could not be answered.')));
        if (err?.dailyUsage) {
          setDailyUsage((current) => normalizeDailyUsage(err.dailyUsage, current));
        }
        return { ok: false, error: err };
      })
      .finally(() => {
        setLoadingFreeQuestion(false);
        inflightFreeQuestionRef.current = null;
      });

    inflightFreeQuestionRef.current = promise;
    return promise;
  }, []);

  const activeInterpretation = useMemo(() => {
    if (!activeQuestionId) return null;
    return interpretationByQuestionId[activeQuestionId] || null;
  }, [activeQuestionId, interpretationByQuestionId]);

  const hasQuestions = questions.length > 0;
  const hasInterpretations = Object.keys(interpretationByQuestionId).length > 0;

  return {
    questions,
    hasQuestions,
    loadingQuestions,
    questionsLoaded,
    activeQuestionId,
    setActiveQuestionId,
    loadingQuestionId,
    interpretationByQuestionId,
    activeInterpretation,
    askedQuestionIds,
    hasInterpretations,
    error,
    dailyUsage,
    setDailyUsage,
    setReading,
    loadQuestions,
    askQuestion,
    clearInterpretations,
    resetState: hardReset,
    freeQuestionInput,
    setFreeQuestionInput,
    loadingFreeQuestion,
    freeQuestionHistory,
    askFreeQuestion,
  };
}
